import React, { useState, useEffect, useRef } from 'react';
import { X, ThumbsUp, ThumbsDown, Clock, User, FileText, History, Layers, Eye, GitCompare } from 'lucide-react';
import { humanFeedbackService } from '../../apis/humanFeedbackService';
import VersionDiffViewer from './version-diff-viewer';

const FeedbackHistoryModal = ({
  isOpen,
  onClose,
  soapNoteId,
  section,
  currentVersion,
  onVersionRestored,
}) => {
  const [activeTab, setActiveTab] = useState('feedback'); // 'feedback' or 'versions'
  const [feedbackHistory, setFeedbackHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [sectionFilter, setSectionFilter] = useState('all');

  // --- For dragging ---
  const modalRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPos, setInitialPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.target.closest(".drag-handle")) {
      setDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setInitialPos({ x: position.x, y: position.y });
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const onMouseMove = (e) => {
    if (dragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPosition({ x: initialPos.x + dx, y: initialPos.y + dy });
    }
  };

  const onMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    } else {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  useEffect(() => {
    if (isOpen && soapNoteId && section) {
      loadFeedbackHistory();
      setSectionFilter('all');
    } else {
      setFeedbackHistory(null);
      setSelectedVersion(null);
      setError(null);
    }
  }, [isOpen, soapNoteId, section]);

  const loadFeedbackHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      if (section === 'assessmentAndPlan') {
        const [assessmentResponse, planResponse] = await Promise.all([
          humanFeedbackService.getSectionFeedbackHistory(soapNoteId, 'assessment'),
          humanFeedbackService.getSectionFeedbackHistory(soapNoteId, 'plan')
        ]);

        const combinedHistory = {
          section: 'assessmentAndPlan',
          currentVersion: Math.max(
            assessmentResponse.data.data.currentVersion || 1,
            planResponse.data.data.currentVersion || 1
          ),
          requestedVersion: null,
          feedbackHistory: [
            ...(assessmentResponse.data.data.feedbackHistory || []).map(f => ({
              ...f,
              originalSection: 'assessment',
              sectionLabel: 'Assessment'
            })),
            ...(planResponse.data.data.feedbackHistory || []).map(f => ({
              ...f,
              originalSection: 'plan',
              sectionLabel: 'Plan'
            }))
          ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        };

        setFeedbackHistory(combinedHistory);
      } else {
        const response = await humanFeedbackService.getSectionFeedbackHistory(soapNoteId, section);
        setFeedbackHistory(response.data.data);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading feedback history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getFeedbackIcon = (type) => {
    return type === 'thumbs_up' ? (
      <ThumbsUp className="w-4 h-4 text-green-600" />
    ) : (
      <ThumbsDown className="w-4 h-4 text-red-600" />
    );
  };

  const getVersionBadgeColor = (version, isCurrent) => {
    if (isCurrent) return 'bg-green-100 text-green-800 border-green-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 pointer-events-none">
      <div
        ref={modalRef}
        onMouseDown={onMouseDown}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: dragging ? "grabbing" : "default",
        }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative pointer-events-auto"
      >
        {/* Draggable Handle */}
        <div className="drag-handle absolute top-0 left-0 right-0 cursor-grab active:cursor-grabbing bg-gray-100 text-gray-600 text-center py-2 rounded-t-lg font-medium z-10 transition-colors hover:bg-gray-200">
          Drag me anywhere ↕️
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-6 pt-10 border-b">
          <div className="flex items-center gap-3">
            <History className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Version History
              </h2>
              <p className="text-sm text-gray-600 capitalize">
                {section === 'assessmentAndPlan' ? 'Assessment & Plan' : section} Section - Current Version: {currentVersion}
              </p>
              {section === 'assessmentAndPlan' && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  Combined feedback from Assessment and Plan sections
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('feedback')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'feedback'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <ThumbsUp className="w-4 h-4" />
                Feedback History
              </div>
            </button>
            <button
              onClick={() => setActiveTab('versions')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'versions'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <GitCompare className="w-4 h-4" />
                Version Changes
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'versions' ? (
            section === 'assessmentAndPlan' ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Section</h3>
                  <VersionDiffViewer
                    soapNoteId={soapNoteId}
                    section="assessment"
                    currentVersion={currentVersion}
                    onVersionRestored={onVersionRestored}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Section</h3>
                  <VersionDiffViewer
                    soapNoteId={soapNoteId}
                    section="plan"
                    currentVersion={currentVersion}
                    onVersionRestored={onVersionRestored}
                  />
                </div>
              </div>
            ) : (
              <VersionDiffViewer
                soapNoteId={soapNoteId}
                section={section}
                currentVersion={currentVersion}
                onVersionRestored={onVersionRestored}
              />
            )
          ) : (
            <>
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {feedbackHistory && !loading && (
                <div className="space-y-6">
                  {/* Version Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-medium text-blue-900 mb-2">Version Summary</h3>
                    <div className="flex items-center gap-4 text-sm text-blue-800">
                      <span>Current Version: <strong>{feedbackHistory.currentVersion}</strong></span>
                      <span>Total Feedback: <strong>{feedbackHistory.feedbackHistory.length}</strong></span>
                    </div>

                    {section === 'assessmentAndPlan' && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-blue-900">Assessment Feedback:</span>
                            <span className="ml-2 text-blue-800">
                              {feedbackHistory.feedbackHistory.filter(f => f.originalSection === 'assessment').length}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-blue-900">Plan Feedback:</span>
                            <span className="ml-2 text-blue-800">
                              {feedbackHistory.feedbackHistory.filter(f => f.originalSection === 'plan').length}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Filter Controls for Combined Section */}
                  {section === 'assessmentAndPlan' && feedbackHistory.feedbackHistory.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Filter by Section</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSectionFilter('all')}
                          className={`px-3 py-1 text-sm rounded-full border transition-colors ${sectionFilter === 'all'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          All ({feedbackHistory.feedbackHistory.length})
                        </button>
                        <button
                          onClick={() => setSectionFilter('assessment')}
                          className={`px-3 py-1 text-sm rounded-full border transition-colors ${sectionFilter === 'assessment'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          Assessment ({feedbackHistory.feedbackHistory.filter(f => f.originalSection === 'assessment').length})
                        </button>
                        <button
                          onClick={() => setSectionFilter('plan')}
                          className={`px-3 py-1 text-sm rounded-full border transition-colors ${sectionFilter === 'plan'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          Plan ({feedbackHistory.feedbackHistory.filter(f => f.originalSection === 'plan').length})
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Feedback History */}
                  {(() => {
                    const filteredHistory = section === 'assessmentAndPlan' && sectionFilter !== 'all'
                      ? feedbackHistory.feedbackHistory.filter(f => f.originalSection === sectionFilter)
                      : feedbackHistory.feedbackHistory;

                    return filteredHistory.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>
                          {sectionFilter !== 'all'
                            ? `No feedback history available for ${sectionFilter} section.`
                            : 'No feedback history available for this section.'
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filteredHistory.map((feedback, index) => (
                          <div
                            key={feedback.feedbackId}
                            className={`border rounded-lg p-4 ${feedback.isCurrentVersion
                                ? 'border-green-200 bg-green-50'
                                : 'border-gray-200 bg-white'
                              }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {getFeedbackIcon(feedback.type)}
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium capitalize">
                                      {feedback.type.replace('_', ' ')}
                                    </span>
                                    <span
                                      className={`px-2 py-1 text-xs rounded-full border ${getVersionBadgeColor(
                                        feedback.sectionVersion,
                                        feedback.isCurrentVersion
                                      )}`}
                                    >
                                      v{feedback.sectionVersion}
                                      {feedback.isCurrentVersion && ' (Current)'}
                                    </span>
                                    {section === 'assessmentAndPlan' && feedback.sectionLabel && (
                                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                                        {feedback.sectionLabel}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                    <div className="flex items-center gap-1">
                                      <User className="w-3 h-3" />
                                      <span>User {feedback.userId?.slice(-8) || 'Unknown'}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      <span>{formatDate(feedback.createdAt)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Feedback Content */}
                            <div className="space-y-3">
                              {feedback.feedbackType === 'paragraph-level' && feedback.reportedContent && (
                                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                                  <p className="text-sm font-medium text-gray-700 mb-1">
                                    Reported Content:
                                  </p>
                                  <p className="text-sm text-gray-600 italic">
                                    "{feedback.reportedContent}"
                                  </p>
                                </div>
                              )}

                              {feedback.explanation && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700 mb-1">
                                    Explanation:
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {feedback.explanation}
                                  </p>
                                </div>
                              )}

                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>Type: {feedback.feedbackType}</span>
                                <span>Content: {feedback.contentType}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackHistoryModal;
